// Globals
import dayjs from 'dayjs';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Helpers
import { sendMessageMapper } from '../../dtos';
import {
  CONTACTS_REQUIRED_KEY,
  getUserId,
  IMessage,
  MAX_MESSAGES_COUNT,
  SocketContext,
  TButton
} from '../../helpers';
import { useMessages, useSocketIO } from '../../hooks';
import { appLocalStorage, toRawPhoneDigits } from '@/utils';

// Models
import { SocketEvents, TGetOperatorStatusResponse, TIdentificationRequestPrefill } from '@/models/chat.model';
import { IUser } from '@/models/user.model';

type TChatProviderProps = {
  children?: ReactNode;
}

const SocketContainer = ({ children }: TChatProviderProps) => {
  const [isBot, setIsBot] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [startPing, setStartPing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { messages, setMessages } = useMessages();
  const [buttons, setButtons] = useState<TButton[]>([]);

  const [userId] = useState(() => getUserId());
  const [showShareContacts, setShowShareContacts] = useState(false);
  const [userInfo, setUserInfo] = useState<TIdentificationRequestPrefill | null>(null);
  const [isSharingContacts, setIsSharingContacts] = useState(false);
  const currentRequestId = useRef<number | null>(null);
  const historySessionId = useRef<null | string>(null);

  const { onSendMessage, onDisconnect, onRate, onCheckConnection, onFetchHistory, onShareContacts, onIdentificationDismissed } = useSocketIO({
    onConnect: ({ socket }) => {
      socket.emit(SocketEvents.JOINED, { userId, messageSource: 'WIDGET' });
      socket.emit(SocketEvents.FETCH_HISTORY, { userId, max_entries: MAX_MESSAGES_COUNT });
      socket.emit(SocketEvents.OPERATOR_STATUS_REQUEST, { userId }, ({ operatorMode }: TGetOperatorStatusResponse) => {
        setIsBot(!operatorMode);
        setStartPing(operatorMode);
      });
      setTimeout(() => {
        ref?.current?.scrollIntoView({ behavior: 'instant' });
      }, 100);
    },

    onStartNewSession() {
      setIsBot(true);
      setUserInfo(null);
      currentRequestId.current = null;
      setShowShareContacts(true);
      appLocalStorage.setItem(CONTACTS_REQUIRED_KEY, historySessionId.current);
    },

    onIdentificationRequest(data) {
      setUserInfo(data.prefill);
      currentRequestId.current = data.requestId;
      setShowShareContacts(true);
    },

    onIdentificationCancelled(data) {
      if (data.requestId !== currentRequestId.current) return;

      setShowShareContacts(false);
      setIsSharingContacts(false);
      currentRequestId.current = null;
      appLocalStorage.removeItem(CONTACTS_REQUIRED_KEY);

      if (data.chatState === 'closed') {
        setUserInfo(null);
      }
    },

    onShareContactsResponse(data) {
      setIsSharingContacts(false);
      setShowShareContacts(false);
      appLocalStorage.removeItem(CONTACTS_REQUIRED_KEY);

      if (!data.message || data.dismissed) return;
      setMessages((prev) => [...prev, {
        message: data.message,
        sender: data.role?.toUpperCase(),
        id: data.messageId,
      }]);
      setTimeout(() => {
        ref?.current?.scrollIntoView({ behavior: 'smooth' });
      }, 250);
    },

    onFetchHistoryResponse(data) {
      if (data.sessionId) {
        historySessionId.current = data.sessionId;
      }
      setMessages((prev) => {
        const currentMessages = data?.history
          .map((message): IMessage => ({
            id: message.messageId,
            message: message.message,
            date: dayjs(message.date).toDate(),
            sender: message?.role?.toUpperCase(),
            type: '',
            attachments: message.attachments,
          }));
        return currentMessages?.length ? currentMessages : prev;
      });
    },

    onOperatorSurvey: (data) => {
      setButtons(Array.from({ length: 2 }).map((_, index) => ({
        text: `${index + 1}`,
        action: 'rate',
        sessionId: data.session_id ?? data.sessionId,
        userId,
        role: 'OPERATOR',
      })));
      setIsTyping(false);
      setIsBot(true);
      setStartPing(false);

      setMessages((prev) => [...prev, {
        message: data.message,
        sender: 'OPERATOR',
        isHistory: true,
        date: new Date(),
        sessionId: data.session_id ?? data.sessionId,
        id: Date.now().toString()
      }]);
      setTimeout(() => {
        ref?.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },

    onAISurvey: (data) => {
      setButtons(Array.from({ length: 10 }).map((_, index) => ({
        text: `${index + 1}`,
        action: 'rate',
        userId,
        sessionId: data.session_id ?? data.sessionId,
        role: 'AI',
      })));
      setIsTyping(false);
      setIsBot(true);

      setMessages((prev) => [...prev, {
        message: data?.message,
        sender: 'AI',
        isHistory: true,
        date: new Date(),
        sessionId: data.session_id ?? data.sessionId,
        id: Date.now().toString()
      }]);
      setTimeout(() => {
        ref?.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },

    onReceiveMessage: (message) => {
      setIsTyping(false);

      setStartPing(message?.role === 'Operator');
      setButtons([]);

      if (!message?.message && !message?.attachments?.length) return;
      setMessages(prev => [...prev, {
        message: message.message,
        sender: message?.role?.toUpperCase(),
        isHistory: isBot ? false : true,
        date: new Date(),
        id: message?.messageId,
        attachments: message?.attachments,
      }]);
      setTimeout(() => {
        ref?.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },

    onConnectionLost: () => {
      setIsBot(true);
    },

    onError: (error) => {
      setIsBot(true);
      console.error(error);
    },
  });

  const handleSendMessage = useCallback((message: string) => {
    if (!onSendMessage) return;
    const newMessage = { message, sender: 'USER', isHistory: false, date: new Date(), id: Date.now().toString() };
    setMessages((prev) => [...prev, newMessage]);
    setButtons([]);
    setIsTyping(true);
    onSendMessage(
      sendMessageMapper({
        messageId: newMessage.id,
        userId,
        isNew: false,
        message,
      })
    );
    const contactsRequiredForSessionId = appLocalStorage.getItem(CONTACTS_REQUIRED_KEY);
    if (contactsRequiredForSessionId && historySessionId.current === contactsRequiredForSessionId) {
      setShowShareContacts(true);
    } else {
      appLocalStorage.removeItem(CONTACTS_REQUIRED_KEY);
    }
  }, [onSendMessage, setMessages, userId]);

  useEffect(() => {
    if (!startPing) return;
    const timer = setInterval(() => {
      onCheckConnection({ userId }, ({ operatorMode }) => {
        setIsBot(!operatorMode)
      })
    }, 5000);

    return () => clearInterval(timer);
  }, [onCheckConnection, startPing, userId]);

  useEffect(() => {
    const timer = setInterval(() => {
      onFetchHistory(userId, MAX_MESSAGES_COUNT);
    }, 5000);

    return () => clearInterval(timer);
  }, [onCheckConnection, onFetchHistory, userId]);

  const handleRate = useCallback((rate: string, isBot: boolean, sessionId: string, userId: string) => {
    setButtons([]);
    onRate({
      isAi: isBot,
      rate,
      userId,
      sessionId: sessionId || '',
      messageSource: 'WIDGET'
    });

    setIsBot(true);
    setMessages((prev) => {
      const newMessages = [...prev, { message: rate, sender: 'USER', isHistory: true, date: new Date(), id: Date.now().toString(), messageType: 'rate' }];
      return newMessages.map((message) => ({ ...message, isHistory: true }));
    });

    setTimeout(() => {
      ref?.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [onRate, setMessages]);

  const handleSaveUser = useCallback((user: IUser) => {
    const phone = user.phone ? toRawPhoneDigits(user.phone) : '';
    const birthdate = user.birthdate ?? '';

    if (!phone && !birthdate) {
      onIdentificationDismissed({
        userId,
        requestId: currentRequestId.current ?? undefined,
        reason: 'saved_empty',
      });
      setShowShareContacts(false);
      appLocalStorage.removeItem(CONTACTS_REQUIRED_KEY);
      return;
    }

    setIsSharingContacts(true);
    onShareContacts({
      userId,
      phone,
      birthdate,
      requestId: currentRequestId.current ?? undefined,
    });
  }, [onIdentificationDismissed, onShareContacts, userId]);

  const handleCloseShareContacts = useCallback(() => {
    setShowShareContacts(false);
    appLocalStorage.removeItem(CONTACTS_REQUIRED_KEY);
    onIdentificationDismissed({
      userId,
      requestId: currentRequestId.current ?? undefined,
      reason: 'closed',
    });
  }, [onIdentificationDismissed, userId])

  const value = useMemo(() => ({
    buttons,
    messages,
    isBot,
    isTyping,
    setUser: handleSaveUser,
    shareContacts: showShareContacts,
    userInfo,
    isSharingContacts,
    sendMessage: handleSendMessage,
    onSendMessage,
    setMessages,
    onDisconnect,
    setButtons,
    setShowShareContacts,
    onCancelShareContacts: handleCloseShareContacts,
    onSendRate: handleRate,
    ref,
  }), [buttons, messages, isBot, isTyping, handleSaveUser, showShareContacts, userInfo, isSharingContacts, handleSendMessage, onSendMessage, setMessages, onDisconnect, handleCloseShareContacts, handleRate]);

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContainer;
